import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Hammer,
  LockKeyhole,
  Mail,
  MapPin,
  ShieldCheck,
  UserRoundCheck,
  WalletCards,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SelectedJobContact, WithdrawInterestButton } from "@/components/jobs/company-interests";
import { getAuthStatus } from "@/lib/auth.functions";
import type { AuthStatus } from "@/lib/auth.types";
import { useLanguage } from "@/lib/i18n";
import { getPublicJob } from "@/lib/jobs.functions";
import {
  formatInterestDeadline,
  jobInterestCopy,
  remainingInterestTime,
} from "@/lib/job-interest.copy";
import {
  expressJobInterest,
  getJobMatching,
  withdrawJobInterest,
} from "@/lib/job-matching.functions";
import type { JobMatchingState } from "@/lib/job-matching.types";
import {
  jobBudgetLabel,
  jobCategoryLabel,
  jobDateLocale,
  jobObjectLabel,
  jobTimelineLabel,
  type JobPublicRow,
} from "@/lib/jobs.types";

export const Route = createFileRoute("/auftraege/$slug")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Auftrag ansehen. Begner" },
      {
        name: "description",
        content: "Einen geprüften Auftrag mit Projektumfang, Budget und Zeitraum ansehen.",
      },
      { property: "og:title", content: "Geprüfter Auftrag. Begner" },
      {
        property: "og:description",
        content: "Projektangaben ansehen und als Auftragnehmer Interesse bekunden.",
      },
    ],
  }),
  component: JobDetailPage,
});

const detailCopy = {
  de: {
    back: "Alle Aufträge",
    verified: "Von Begner geprüft",
    original: "Originalinhalt",
    published: "Publiziert am",
    timeline: "Zeitrahmen",
    budget: "Budget",
    object: "Objekt",
    description: "Projektbeschreibung",
    location: "Ausführungsort",
    notFoundTitle: "Dieser Auftrag ist nicht verfügbar.",
    notFoundText: "Er wurde möglicherweise vergeben, zurückgezogen oder ist abgelaufen.",
    errorTitle: "Der Auftrag konnte nicht geladen werden.",
    errorText: "Bitte versuchen Sie es in einem Moment erneut.",
    postTitle: "Sie planen selbst ein Projekt?",
    postText: "Beschreiben Sie Ihren Auftrag kostenlos und erhalten Sie passende Offerten.",
    postButton: "Anfrage eingeben",
    contactEyebrow: "Kundenkontakt",
    anonymousTitle: "Interesse als Auftragnehmer",
    anonymousText:
      "Erstellen Sie ein Firmenkonto oder melden Sie sich an, um mit Pro Interesse zu bekunden.",
    register: "Jetzt registrieren",
    login: "Anmelden",
    pendingTitle: "Ihr Profil wird noch geprüft",
    pendingText:
      "Nach der Firmenprüfung können Sie mit Pro Interesse bekunden. Kundenkontakte erhalten nur ausgewählte Firmen.",
    account: "Prüfstatus ansehen",
    rejectedTitle: "Ihr Profil wurde nicht freigegeben",
    rejectedText:
      "Prüfen Sie den Hinweis in unserer E-Mail oder reichen Sie ein überarbeitetes Unternehmensprofil ein.",
    suspendedTitle: "Firmenzugang vorübergehend gesperrt",
    suspendedText:
      "Bitte kontaktieren Sie Begner, damit wir den Status Ihres Kontos prüfen können.",
    contactSupport: "Begner kontaktieren",
    unverifiedTitle: "E-Mail-Bestätigung erforderlich",
    unverifiedText:
      "Bestätigen Sie zuerst Ihre E-Mail-Adresse, damit wir Ihr Konto sicher zuordnen können.",
    completeProfile: "Unternehmensprofil vervollständigen",
    membershipTitle: "Pro-Zugang erforderlich",
    membershipText:
      "Geprüfte Firmen können mit Pro Interesse bekunden. Kontaktdaten erhalten Sie erst nach Auswahl durch den Auftraggeber.",
    membership: "Zahlungsinfo ansehen",
  },
  fr: {
    back: "Tous les mandats",
    verified: "Vérifié par Begner",
    original: "Contenu original",
    published: "Publié le",
    timeline: "Calendrier",
    budget: "Budget",
    object: "Objet",
    description: "Description du projet",
    location: "Lieu d'exécution",
    notFoundTitle: "Ce mandat n'est pas disponible.",
    notFoundText: "Il a peut-être été attribué, retiré ou est arrivé à expiration.",
    errorTitle: "Impossible de charger ce mandat.",
    errorText: "Veuillez réessayer dans quelques instants.",
    postTitle: "Vous planifiez votre propre projet ?",
    postText: "Décrivez gratuitement votre mandat et recevez des offres adaptées.",
    postButton: "Publier un mandat",
    contactEyebrow: "Contact client",
    anonymousTitle: "Intérêt en tant que prestataire",
    anonymousText:
      "Créez un compte entreprise ou connectez-vous pour manifester un intérêt avec Pro.",
    register: "S'inscrire maintenant",
    login: "Se connecter",
    pendingTitle: "Votre profil est en cours de vérification",
    pendingText:
      "Après validation de votre entreprise, vous pouvez manifester un intérêt avec Pro. Seules les entreprises sélectionnées reçoivent les coordonnées.",
    account: "Voir le statut",
    rejectedTitle: "Votre profil n’a pas été validé",
    rejectedText:
      "Consultez la remarque dans notre e-mail ou soumettez un profil d’entreprise corrigé.",
    suspendedTitle: "Accès aux contacts temporairement suspendu",
    suspendedText: "Contactez Begner afin que nous puissions vérifier le statut de votre compte.",
    contactSupport: "Contacter Begner",
    unverifiedTitle: "Confirmation de l'e-mail requise",
    unverifiedText:
      "Confirmez d'abord votre adresse e-mail afin que nous puissions sécuriser votre compte.",
    completeProfile: "Compléter le profil d’entreprise",
    membershipTitle: "Accès Pro requis",
    membershipText:
      "Avec Pro, les prestataires validés peuvent manifester un intérêt. Les coordonnées sont disponibles uniquement après sélection par le client.",
    membership: "Voir les informations de paiement",
  },
  it: {
    back: "Tutti gli incarichi",
    verified: "Verificato da Begner",
    original: "Contenuto originale",
    published: "Pubblicato il",
    timeline: "Tempistica",
    budget: "Budget",
    object: "Oggetto",
    description: "Descrizione del progetto",
    location: "Luogo di esecuzione",
    notFoundTitle: "Questo incarico non è disponibile.",
    notFoundText: "Potrebbe essere stato assegnato, ritirato o essere scaduto.",
    errorTitle: "Non è stato possibile caricare l'incarico.",
    errorText: "Riprovi tra qualche istante.",
    postTitle: "Sta pianificando un suo progetto?",
    postText: "Descriva gratuitamente il suo incarico e riceva offerte adatte.",
    postButton: "Pubblica un incarico",
    contactEyebrow: "Contatto cliente",
    anonymousTitle: "Interesse come impresa",
    anonymousText: "Crei un account impresa o acceda per manifestare interesse con Pro.",
    register: "Registrati ora",
    login: "Accedi",
    pendingTitle: "Il suo profilo è ancora in verifica",
    pendingText:
      "Dopo la verifica dell’impresa può manifestare interesse con Pro. Solo le imprese selezionate ricevono i contatti.",
    account: "Vedi stato verifica",
    rejectedTitle: "Il suo profilo non è stato approvato",
    rejectedText:
      "Consulti la nota nella nostra e-mail oppure invii un profilo aziendale aggiornato.",
    suspendedTitle: "Accesso ai contatti temporaneamente sospeso",
    suspendedText: "Contatti Begner per consentirci di verificare lo stato del suo account.",
    contactSupport: "Contatta Begner",
    unverifiedTitle: "È richiesta la conferma dell'e-mail",
    unverifiedText:
      "Confermi prima l'indirizzo e-mail per consentirci di proteggere il suo account.",
    completeProfile: "Completa il profilo aziendale",
    membershipTitle: "È richiesto l’accesso Pro",
    membershipText:
      "Con Pro le imprese approvate possono manifestare interesse. I contatti sono disponibili solo dopo la selezione del cliente.",
    membership: "Vedi dati di pagamento",
  },
  en: {
    back: "All jobs",
    verified: "Verified by Begner",
    original: "Original content",
    published: "Published on",
    timeline: "Timeline",
    budget: "Budget",
    object: "Property",
    description: "Project description",
    location: "Work location",
    notFoundTitle: "This job is not available.",
    notFoundText: "It may have been awarded, withdrawn or expired.",
    errorTitle: "The job could not be loaded.",
    errorText: "Please try again in a moment.",
    postTitle: "Planning your own project?",
    postText: "Describe your job free of charge and receive suitable quotes.",
    postButton: "Post a job",
    contactEyebrow: "Customer contact",
    anonymousTitle: "Interest as a contractor",
    anonymousText: "Create a company account or log in to express interest with Pro.",
    register: "Register now",
    login: "Log in",
    pendingTitle: "Your profile is still under review",
    pendingText:
      "After company approval, Pro lets you express interest. Only companies selected by the customer receive contact details.",
    account: "View verification status",
    rejectedTitle: "Your profile was not approved",
    rejectedText: "Review the note in our email or submit an updated company profile.",
    suspendedTitle: "Contact access temporarily suspended",
    suspendedText: "Please contact Begner so we can review your account status.",
    contactSupport: "Contact Begner",
    unverifiedTitle: "Email confirmation required",
    unverifiedText:
      "Please confirm your email address first so we can securely identify your account.",
    completeProfile: "Complete company profile",
    membershipTitle: "Pro access required",
    membershipText:
      "Approved contractors with Pro can express interest. Contact details are available only after the customer selects your company.",
    membership: "View payment details",
  },
} as const;

function Gallery({ job }: { job: JobPublicRow }) {
  const [active, setActive] = useState(0);
  const [failed, setFailed] = useState<Set<string>>(() => new Set());
  const usableImages = job.images.filter((image) => !failed.has(image));
  const selected = usableImages[Math.min(active, Math.max(usableImages.length - 1, 0))];

  const markFailed = (image: string) => {
    setFailed((current) => new Set(current).add(image));
    setActive(0);
  };

  return (
    <div className="space-y-2.5">
      <div className="relative aspect-[16/9] max-h-[460px] overflow-hidden rounded-2xl border border-border bg-muted shadow-card">
        {selected ? (
          <img
            src={selected}
            alt={`${job.title} – ${active + 1}`}
            className="h-full w-full object-cover"
            onError={() => markFailed(selected)}
          />
        ) : (
          <div className="relative flex h-full items-center justify-center overflow-hidden bg-gradient-to-br from-[#eef8fb] via-[#dff4fa] to-[#c8e8f1] text-brand-deep">
            <div className="absolute -right-16 -top-20 h-72 w-72 rounded-full bg-white/65 blur-3xl" />
            <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-brand/15 blur-3xl" />
            <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl border border-white/80 bg-white/75 shadow-card backdrop-blur">
              <Hammer className="h-9 w-9" strokeWidth={1.6} />
            </div>
          </div>
        )}
        {usableImages.length > 1 ? (
          <span className="absolute bottom-4 right-4 rounded-full bg-foreground/70 px-3 py-1 text-xs font-semibold text-background backdrop-blur">
            {Math.min(active + 1, usableImages.length)} / {usableImages.length}
          </span>
        ) : null}
      </div>

      {usableImages.length > 1 ? (
        <div className="flex snap-x gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {usableImages.map((image, index) => (
            <button
              key={image}
              type="button"
              onClick={() => setActive(index)}
              className={`h-14 w-20 shrink-0 snap-start overflow-hidden rounded-lg border-2 bg-muted transition ${
                index === active
                  ? "border-brand shadow-brand"
                  : "border-transparent opacity-75 hover:opacity-100"
              }`}
              aria-label={`${index + 1} / ${usableImages.length}`}
            >
              <img
                src={image}
                alt=""
                className="h-full w-full object-cover"
                onError={() => markFailed(image)}
              />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ContactPlaceholder() {
  return (
    <div
      className="pointer-events-none select-none space-y-3 rounded-2xl border border-border bg-background/85 p-4 blur-[5px]"
      aria-hidden
    >
      <div className="h-4 w-2/5 rounded-full bg-foreground/45" />
      <div className="h-4 w-4/5 rounded-full bg-foreground/35" />
      <div className="h-4 w-3/5 rounded-full bg-foreground/30" />
    </div>
  );
}

function LockedMessage({
  icon: Icon,
  title,
  text,
  children,
}: {
  icon: typeof LockKeyhole;
  title: string;
  text: string;
  children?: ReactNode;
}) {
  return (
    <div className="mt-4">
      <div className="relative overflow-hidden rounded-2xl border border-border bg-background/70 p-4">
        <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 opacity-65" aria-hidden>
          <ContactPlaceholder />
        </div>
        <div className="relative z-10 flex flex-col items-center rounded-xl bg-card/75 px-3 py-4 text-center shadow-sm backdrop-blur-[3px]">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft text-brand-deep shadow-sm">
            <Icon className="h-5 w-5" />
          </div>
          <h3 className="mt-3 font-display text-lg font-bold leading-tight">{title}</h3>
          <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-muted-foreground">{text}</p>
        </div>
      </div>
      {children ? <div className="mt-4">{children}</div> : null}
    </div>
  );
}

function ContactPanel({ jobId, slug }: { jobId: string; slug: string }) {
  const { language } = useLanguage();
  const copy = detailCopy[language];
  const interestCopy = jobInterestCopy[language];
  const checkAuth = useServerFn(getAuthStatus);
  const loadMatching = useServerFn(getJobMatching);
  const express = useServerFn(expressJobInterest);
  const withdraw = useServerFn(withdrawJobInterest);
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [matching, setMatching] = useState<JobMatchingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pending, setPending] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);
  const [clockOffset, setClockOffset] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const latestRefresh = useRef(0);
  const returnTo = `/auftraege/${slug}`;
  const billingUrl = `/konto?next=${encodeURIComponent(returnTo)}#billing`;

  const refresh = useCallback(async () => {
    const request = ++latestRefresh.current;
    try {
      const [account, state] = await Promise.all([checkAuth(), loadMatching({ data: { jobId } })]);
      if (request !== latestRefresh.current) return;
      setAuth(account);
      setMatching(state);
      setClockOffset(Date.parse(state.serverNow) - Date.now());
      setNow(Date.now());
      setLoadFailed(false);
    } catch {
      if (request === latestRefresh.current) setLoadFailed(true);
    } finally {
      if (request === latestRefresh.current) setLoading(false);
    }
  }, [checkAuth, jobId, loadMatching]);

  useEffect(() => {
    setLoading(true);
    setMatching(null);
    setActionFailed(false);
    void refresh();
    const poll = window.setInterval(() => {
      if (!document.hidden) void refresh();
    }, 60_000);
    const tick = window.setInterval(() => setNow(Date.now()), 15_000);
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      latestRefresh.current += 1;
      window.clearInterval(poll);
      window.clearInterval(tick);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  async function expressInterest() {
    latestRefresh.current += 1;
    setPending(true);
    setActionFailed(false);
    try {
      const result = await express({ data: { jobId } });
      latestRefresh.current += 1;
      window.dispatchEvent(new Event("job-notifications-updated"));
      setMatching(result);
      setClockOffset(Date.parse(result.serverNow) - Date.now());
    } catch {
      setActionFailed(true);
      await refresh();
    } finally {
      setPending(false);
    }
  }

  async function withdrawInterest() {
    latestRefresh.current += 1;
    setActionFailed(false);
    try {
      const result = await withdraw({ data: { jobId } });
      latestRefresh.current += 1;
      setMatching(result);
      window.dispatchEvent(new Event("job-notifications-updated"));
    } catch (error) {
      setActionFailed(true);
      await refresh();
      throw error;
    }
  }

  const own = matching?.ownInterest;
  const collecting =
    matching?.phase === "collecting" && Date.parse(matching.interestDeadline) > now + clockOffset;
  const verified =
    auth?.authenticated &&
    auth.user.emailConfirmed &&
    auth.profile?.role === "auftragnehmer" &&
    auth.profile.providerStatus === "approved";
  let content: ReactNode;

  if (loading) {
    content = (
      <p className="mt-4 text-sm text-muted-foreground" role="status">
        {interestCopy.loading}
      </p>
    );
  } else if (loadFailed || !matching) {
    content = (
      <div className="mt-4 space-y-3">
        <p className="text-sm text-destructive" role="alert">
          {interestCopy.loadError}
        </p>
        <Button type="button" variant="outline" onClick={() => void refresh()}>
          {interestCopy.retry}
        </Button>
      </div>
    );
  } else if (matching.isOwner) {
    content = (
      <LockedMessage
        icon={BriefcaseBusiness}
        title={interestCopy.ownerTitle}
        text={interestCopy.ownerText}
      >
        <Button asChild className="w-full rounded-xl">
          <a href={`/konto#auftrag-${jobId}`}>{interestCopy.manage}</a>
        </Button>
      </LockedMessage>
    );
  } else if (matching.phase === "ended" || own?.status === "ended") {
    content = (
      <LockedMessage
        icon={ShieldCheck}
        title={interestCopy.endedTitle}
        text={interestCopy.endedText}
      />
    );
  } else if (own?.contactRevoked) {
    content = (
      <LockedMessage
        icon={ShieldCheck}
        title={interestCopy.revokedTitle}
        text={interestCopy.revokedText}
      />
    );
  } else if (own?.status === "selected") {
    content = (
      <LockedMessage
        icon={UserRoundCheck}
        title={interestCopy.selectedTitle}
        text={interestCopy.selectedText}
      >
        {verified ? (
          <SelectedJobContact jobId={jobId} allowed />
        ) : (
          <p className="text-sm text-muted-foreground">{copy.suspendedText}</p>
        )}
      </LockedMessage>
    );
  } else if (own?.status === "pending") {
    content = (
      <LockedMessage
        icon={Clock3}
        title={interestCopy.pendingTitle}
        text={interestCopy.pendingText}
      >
        <WithdrawInterestButton onWithdraw={withdrawInterest} />
        <Button asChild variant="ghost" className="mt-2 w-full">
          <a href="/konto#job-interests">{interestCopy.mineTitle}</a>
        </Button>
      </LockedMessage>
    );
  } else if (own?.status === "not_selected") {
    content = (
      <LockedMessage
        icon={ShieldCheck}
        title={interestCopy.notSelectedTitle}
        text={interestCopy.notSelectedText}
      />
    );
  } else if (!collecting) {
    content = (
      <LockedMessage
        icon={Clock3}
        title={interestCopy.closedTitle}
        text={interestCopy.closedText}
      />
    );
  } else if (!auth?.authenticated) {
    content = (
      <LockedMessage icon={LockKeyhole} title={copy.anonymousTitle} text={interestCopy.readyText}>
        <Button asChild className="w-full rounded-xl">
          <Link to="/login" search={{ mode: "registrieren", typ: "auftragnehmer", next: returnTo }}>
            {interestCopy.express}
            <ArrowRight className="size-4" />
          </Link>
        </Button>
        <Button asChild variant="outline" className="mt-2 w-full rounded-xl">
          <Link to="/login" search={{ typ: "auftragnehmer", next: returnTo }}>
            {copy.login}
          </Link>
        </Button>
      </LockedMessage>
    );
  } else if (!auth.user.emailConfirmed) {
    content = <LockedMessage icon={Mail} title={copy.unverifiedTitle} text={copy.unverifiedText} />;
  } else if (!auth.profile || auth.profile.role !== "auftragnehmer") {
    content = (
      <LockedMessage
        icon={BriefcaseBusiness}
        title={interestCopy.wrongRoleTitle}
        text={interestCopy.wrongRoleText}
      >
        <Button asChild variant="outline" className="w-full">
          <a href="/konto">{copy.account}</a>
        </Button>
      </LockedMessage>
    );
  } else if (auth.profile.providerStatus === "pending") {
    content = (
      <LockedMessage icon={BadgeCheck} title={copy.pendingTitle} text={copy.pendingText}>
        <Button asChild variant="outline" className="w-full rounded-xl">
          <a href={`/konto?next=${encodeURIComponent(returnTo)}`}>{copy.account}</a>
        </Button>
      </LockedMessage>
    );
  } else if (auth.profile.providerStatus === "rejected") {
    content = (
      <LockedMessage icon={BadgeCheck} title={copy.rejectedTitle} text={copy.rejectedText}>
        <Button asChild className="w-full rounded-xl">
          <a href={`/registrieren?typ=auftragnehmer&next=${encodeURIComponent(returnTo)}`}>
            {copy.completeProfile}
          </a>
        </Button>
      </LockedMessage>
    );
  } else if (auth.profile.providerStatus === "suspended") {
    content = (
      <LockedMessage icon={ShieldCheck} title={copy.suspendedTitle} text={copy.suspendedText}>
        <Button asChild variant="outline" className="w-full rounded-xl">
          <a href="mailto:info@begner.ch">{copy.contactSupport}</a>
        </Button>
      </LockedMessage>
    );
  } else if (!auth.canExpressInterest) {
    content = (
      <LockedMessage icon={ShieldCheck} title={copy.membershipTitle} text={copy.membershipText}>
        <Button asChild className="w-full rounded-xl">
          <a href={billingUrl}>
            {interestCopy.express}
            <ArrowRight className="size-4" />
          </a>
        </Button>
        <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
          {interestCopy.paymentHint}
        </p>
      </LockedMessage>
    );
  } else {
    content = (
      <LockedMessage
        icon={own?.status === "withdrawn" ? Clock3 : BriefcaseBusiness}
        title={own?.status === "withdrawn" ? interestCopy.withdrawnTitle : interestCopy.readyTitle}
        text={own?.status === "withdrawn" ? interestCopy.withdrawnText : interestCopy.readyText}
      >
        <Button
          type="button"
          className="w-full rounded-xl"
          onClick={() => void expressInterest()}
          disabled={pending}
        >
          {pending ? interestCopy.expressing : interestCopy.express}
          <ArrowRight className="size-4" />
        </Button>
      </LockedMessage>
    );
  }

  return (
    <aside className="rounded-3xl border border-border bg-card p-5 shadow-card lg:sticky lg:top-24">
      <span className="text-xs font-semibold uppercase tracking-wider text-brand-deep">
        {interestCopy.heading}
      </span>
      {matching && (matching.phase === "collecting" || matching.phase === "selection") ? (
        <div className="mt-3 rounded-xl bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
          <p>
            {matching.phase === "collecting"
              ? interestCopy.interestDeadline
              : interestCopy.selectionDeadline}
          </p>
          <p className="mt-1 font-semibold text-foreground">
            {formatInterestDeadline(
              matching.phase === "collecting"
                ? matching.interestDeadline
                : matching.selectionDeadline,
              language,
            )}
          </p>
          {collecting ? (
            <p className="mt-1">
              {interestCopy.remaining}:{" "}
              {remainingInterestTime(matching.interestDeadline, now + clockOffset)}
            </p>
          ) : null}
        </div>
      ) : null}
      {content}
      {actionFailed ? (
        <p className="mt-3 text-sm text-destructive" role="alert">
          {interestCopy.error}
        </p>
      ) : null}
      <p className="mt-4 flex items-start gap-1.5 text-center text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" />
        {interestCopy.privacy}
      </p>
    </aside>
  );
}

function JobDetailPage() {
  const { language } = useLanguage();
  const copy = detailCopy[language];
  const { slug } = Route.useParams();
  const loadJob = useServerFn(getPublicJob);
  const [job, setJob] = useState<JobPublicRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setFailed(false);
    void loadJob({ data: { slug } })
      .then((result) => {
        if (active) setJob(result.job);
      })
      .catch(() => {
        if (!active) return;
        setJob(null);
        setFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [loadJob, slug]);

  if (loading) {
    return (
      <section className="bg-surface py-10 md:py-14">
        <div className="container-page">
          <div className="h-5 w-36 animate-pulse rounded bg-muted" />
          <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div>
              <div className="aspect-[16/10] animate-pulse rounded-3xl bg-muted" />
              <div className="mt-7 h-10 w-4/5 animate-pulse rounded bg-muted" />
              <div className="mt-4 h-24 animate-pulse rounded-2xl bg-muted/70" />
            </div>
            <div className="h-80 animate-pulse rounded-3xl border border-border bg-card" />
          </div>
        </div>
      </section>
    );
  }

  if (!job) {
    return (
      <section className="bg-surface py-20">
        <div className="container-page max-w-2xl text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-soft text-brand-deep">
            <ClipboardIcon />
          </div>
          <h1 className="mt-6 font-display text-4xl font-bold">
            {failed ? copy.errorTitle : copy.notFoundTitle}
          </h1>
          <p className="mt-4 text-muted-foreground">
            {failed ? copy.errorText : copy.notFoundText}
          </p>
          <Button asChild className="mt-8 rounded-xl">
            <Link to="/auftraege">
              <ArrowLeft className="h-4 w-4" />
              {copy.back}
            </Link>
          </Button>
        </div>
      </section>
    );
  }

  const publishedAt = job.published_at || job.created_at;
  const location = [job.postal_code, job.city].filter(Boolean).join(" ");

  return (
    <>
      <section className="border-b border-border bg-surface">
        <div className="container-page py-7 md:py-9">
          <Link
            to="/auftraege"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep transition hover:text-brand"
          >
            <ArrowLeft className="h-4 w-4" />
            {copy.back}
          </Link>
        </div>
      </section>

      <section className="bg-background py-6 md:py-8">
        <div className="container-page grid min-w-0 gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
          <main className="min-w-0">
            <Gallery job={job} />

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Badge className="bg-brand-soft text-brand-deep hover:bg-brand-soft">
                {jobCategoryLabel(job.category, language)}
              </Badge>
              <Badge variant="outline" className="gap-1.5 bg-card">
                <CheckCircle2 className="h-3.5 w-3.5 text-brand-deep" />
                {copy.verified}
              </Badge>
              <Badge variant="outline" className="bg-card uppercase">
                {copy.original}: {(job.locale || "de").toUpperCase()}
              </Badge>
            </div>

            <h1 className="mt-4 max-w-4xl font-display text-3xl font-bold tracking-tight md:text-4xl">
              {job.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-brand-deep" />
                {location}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4 text-brand-deep" />
                {copy.published}{" "}
                {new Date(publishedAt).toLocaleDateString(jobDateLocale(language), {
                  timeZone: "Europe/Zurich",
                })}
              </span>
            </div>

            <dl className="mt-6 grid gap-2 sm:grid-cols-3">
              {[
                {
                  label: copy.timeline,
                  value: jobTimelineLabel(job.timeline, language),
                  icon: Clock3,
                },
                {
                  label: copy.budget,
                  value: jobBudgetLabel(job.budget, language),
                  icon: WalletCards,
                },
                {
                  label: copy.object,
                  value: jobObjectLabel(job.object_type, language),
                  icon: BriefcaseBusiness,
                },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-xl border border-border bg-surface p-3.5">
                  <dt className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <Icon className="h-4 w-4 text-brand-deep" />
                    {label}
                  </dt>
                  <dd className="mt-1.5 text-sm font-semibold leading-snug text-foreground">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-card md:p-6">
              <h2 className="font-display text-xl font-bold">{copy.description}</h2>
              <p className="mt-3 whitespace-pre-wrap text-[0.95rem] leading-7 text-foreground/80">
                {job.description}
              </p>
              <div className="mt-5 flex items-center gap-3 rounded-xl bg-surface p-3.5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand-deep">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {copy.location}
                  </p>
                  <p className="font-semibold">{location}</p>
                </div>
              </div>
            </div>
          </main>

          <ContactPanel jobId={job.id} slug={job.slug} />
        </div>
      </section>

      <section className="border-t border-border bg-surface py-8">
        <div className="container-page flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-card md:flex-row md:items-center md:justify-between md:p-6">
          <div>
            <h2 className="font-display text-xl font-bold">{copy.postTitle}</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">{copy.postText}</p>
          </div>
          <Button asChild size="lg" className="h-12 shrink-0 rounded-xl">
            <Link to="/auftrag-einstellen">
              {copy.postButton}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </>
  );
}

function ClipboardIcon() {
  return <BriefcaseBusiness className="h-8 w-8" />;
}
